import { Select } from 'antd'
import React, { useMemo } from 'react'

const { Option } = Select

/**
 * 自定义选择器组件
 *
 * 这是一个基于Ant Design Select封装的选项选择器组件，支持通过options数组配置选项。
 * 提供了灵活的字段名映射功能，支持自定义label和value字段名。
 *
 * @param options - 选项数据数组，每个选项包含label和value字段
 * @param defaultValue - 默认选中的值
 * @param onChange - 选择变化时的回调函数
 * @param fieldNames - 字段名映射，用于自定义label和value字段名
 * @param props - 其他传递给Select组件的属性
 *
 * 使用场景：
 * 1. 表单选择 - 表单中的下拉选择器
 * 2. 数据筛选 - 数据列表的筛选条件
 * 3. 分类选择 - 商品分类、文章分类等
 * 4. 状态选择 - 订单状态、用户状态等
 * 5. 地区选择 - 省市区选择器
 * 6. 角色选择 - 用户角色、权限角色选择
 * 7. 配置选择 - 系统配置项的选择
 * 8. 搜索过滤 - 搜索条件的过滤选择
 *
 * 使用示例：
 * ```tsx
 * // 基础用法
 * <CustomSelect
 *   defaultValue="lucy"
 *   style={{ width: 120 }}
 *   onChange={(value) => console.log(value)}
 *   options={[
 *     { value: 'jack', label: 'Jack' },
 *     { value: 'lucy', label: 'Lucy' },
 *     { value: 'yiminghe', label: 'Yiminghe' },
 *     { value: 'disabled', label: 'Disabled', disabled: true }
 *   ]}
 * />
 *
 * // 自定义字段名
 * <CustomSelect
 *   options={[
 *     { id: 1, name: '选项1' },
 *     { id: 2, name: '选项2' }
 *   ]}
 *   fieldNames={{ label: 'name', value: 'id' }}
 *   onChange={handleChange}
 * />
 *
 * // 多选模式
 * <CustomSelect
 *   mode="multiple"
 *   placeholder="请选择多个选项"
 *   options={options}
 *   onChange={handleMultipleChange}
 * />
 *
 * // 搜索功能
 * <CustomSelect
 *   showSearch
 *   placeholder="搜索并选择"
 *   options={options}
 *   filterOption={(input, option) =>
 *     option?.label?.toLowerCase().includes(input.toLowerCase())
 *   }
 * />
 * ```
 */

// 定义封装后组件的属性类型
interface CustomSelectProps extends Omit<React.ComponentProps<typeof Select>, 'children'> {
  options: { [key: string]: any }[] // 选项数据数组
  defaultValue?: any // 默认选中的值
  onChange?: (value: any) => void // 选择变化回调
  fieldNames?: { label: string; value: string } // 字段名映射
}

const CustomSelect: React.FC<CustomSelectProps> = ({ options, defaultValue, onChange, fieldNames, ...props }) => {
  // 解构字段名映射，默认使用label和value字段
  const { label: labelKey = 'label', value: valueKey = 'value' } = fieldNames || {}

  // 使用useMemo优化选项渲染，避免不必要的重新渲染
  const renderedOptions = useMemo(
    () =>
      options.map(item => (
        <Option key={item[valueKey]} value={item[valueKey]}>
          {item[labelKey]}
        </Option>
      )),
    [options, labelKey, valueKey]
  )

  return (
    <Select defaultValue={defaultValue} onChange={onChange} {...props}>
      {renderedOptions}
    </Select>
  )
}

export default CustomSelect
